import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { ArrowRight, Check, Sparkles, Calendar, Clock, Building2, Mail } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { useLanguage } from '../../context/LanguageContext';
import { submitDemoRequest } from '../../lib/api';

export const CTASection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { t, language } = useLanguage();
  const data = t('cta');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    
    setIsLoading(true);
    setError('');
    try {
      await submitDemoRequest({ email, company, source: 'landing_page', language });
      setIsSubmitted(true);
    } catch (err) {
      setError(data.errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <section
      id="demo"
      data-testid="cta-section"
      className="relative py-24 sm:py-32 overflow-hidden bg-zinc-950"
    >
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
        >
          {/* Headline */}
          <div className="text-center mb-10">
            <h2
              data-testid="cta-headline"
              className="text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight mb-4 text-zinc-100"
            >
              {data.headline}{' '}
              <span className="font-semibold gradient-text">
                {data.headlineHighlight}
              </span>
            </h2>
            <p className="text-zinc-400 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
              {data.subheadline}
            </p>
          </div>

          {/* Form Card */}
          {!isSubmitted ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: 0.3 }}
              className="max-w-md mx-auto"
            >
              <form
                onSubmit={handleSubmit}
                data-testid="demo-form"
                className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 sm:p-8"
              >
                <div className="space-y-4">
                  <div className="relative">
                    <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="email"
                      placeholder={data.emailPlaceholder}
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      data-testid="demo-form-email"
                      className="w-full bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-400 rounded-xl py-3 pl-10 pr-4 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div className="relative">
                    <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-400" />
                    <Input
                      type="text"
                      placeholder={data.companyPlaceholder}
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                      data-testid="demo-form-company"
                      className="w-full bg-zinc-950 border-zinc-800 text-zinc-100 placeholder:text-zinc-400 rounded-xl py-3 pl-10 pr-4 focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <Button
                    type="submit"
                    disabled={isLoading}
                    data-testid="demo-form-submit"
                    className="w-full py-3 bg-zinc-950 rounded-xl font-medium text-sm inline-flex items-center justify-center gap-2 group"
                  >
                    {isLoading ? (
                      <motion.div
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
                        className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full"
                      />
                    ) : (
                      <>
                        {data.submit}
                        <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </>
                    )}
                  </Button>
                </div>
                
                {/* Trust indicators */}
                <div className="mt-5 pt-4 flex flex-wrap justify-center gap-4 text-xs text-zinc-400">
                  <span className="flex items-center gap-1.5">
                    <Calendar className="w-3 h-3 text-blue-400" />
                    {data.trustIndicators[0]}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-3 h-3 text-blue-400" />
                    {data.trustIndicators[1]}
                  </span>
                </div>
              </form>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5 }}
              className="max-w-md mx-auto"
              data-testid="demo-form-success"
            >
              <div className="bg-emerald-900/20 border border-emerald-800 rounded-2xl p-8 text-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: 'spring', delay: 0.2 }}
                  className="w-16 h-16 rounded-full bg-emerald-900/40 flex items-center justify-center mx-auto mb-5"
                >
                  <Check className="w-8 h-8 text-emerald-600" />
                </motion.div>
                <h3 className="text-xl font-semibold text-zinc-100 mb-2">{data.successTitle}</h3>
                <p className="text-sm text-zinc-400">
                  {data.successMessage}
                </p>
              </div>
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
};

export default CTASection;
